/** Spreadsheet upload and editable validation before direct cohort enrollment. */
import { useEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/page-header'
import { OPERATOR_PAGE_MAIN, OPERATOR_WORKFLOW_PAGE } from '../common/page-layout'
import { ProvisioningFormActions } from '../common/provisioning-form-actions'
import { Link, useSearchParams } from 'react-router-dom'
import { AdminApiError } from '../api/client'
import { keysStaleAfterStudentChange } from '../api/query-keys'
import { ClientPicker } from '../clients/client-picker'
import { useClient } from '../clients/use-client'
import { CohortPicker } from '../cohorts/cohort-picker'
import { cohortEnrollmentReadinessProblems } from '../cohorts/enrollment-readiness'
import { queryKeys as adminKeys } from '../api/query-keys'
import { useCohort } from '../cohorts/use-cohort'
import { useResetOnChange } from '../common/use-reset-on-change'
import {
  AccessWindowCell,
  AccessWindowPanel,
  formatBusinessDate,
  formatWindowLength,
  todayJstIso,
} from '../common/access-window'
import { SubmitButton } from '../common/save-button'
import {
  UnsavedChangesGuard,
  useLeaveAfterSave,
} from '../common/unsaved-changes'
import { FormError, formMessage, messageFor, resolveFailure, type ResolvedFailure } from '../common/field-errors'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW_NESTED, TABLE_WRAP } from '../common/table'
import { Badge, Button, FieldError, Icon, Input, Notice, NoticeAction, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, useToast } from '@speaking-coach/ui'
import { adminStudentClient, messageForStudentError } from './api'
import {
  createStudentBatchSchema,
  type CreateStudentBatchForm,
  type CreateStudentBatchFormInput,
} from './batch-schema'
import { EnrollmentFileInput } from './enrollment-file-input'
import type { EnrollmentFileRow } from './enrollment-file'
import { CohortEnrollmentDefaultsPreview } from './cohort-enrollment-defaults-preview'
import { InvitationScheduleSummary } from './invitation-schedule-summary'
import type {
  BatchRowErrorPayload,
  EnrollmentBatchPreflightResponse,
} from './types'

/** Cohort-scope codes render at the cohort field; row codes are anchored
 *  to their table rows from the error payload instead. */
const BATCH_ERROR_FIELD = {
  cohort_not_found: 'cohortId',
  invalid_cohort_id: 'cohortId',
  cohort_access_ended: 'cohortId',
} as const

type BatchField = (typeof BATCH_ERROR_FIELD)[keyof typeof BATCH_ERROR_FIELD]

/** Per-ROW wording for the codes the server anchors to rows. */
const ROW_CODE_MESSAGE: Record<string, string> = {
  invalid_email: 'Not a valid email address.',
  invalid_name: 'Name is required.',
  duplicate_emails_in_batch: 'Duplicated in this list.',
  email_already_registered: 'Cannot be enrolled in this client.',
  period_overlap: 'Overlaps an existing enrollment.',
  placement_reuse_unavailable:
    'The reviewed placement result is no longer reusable. Check the rows again.',
}

function isRowPayload(payload: unknown): payload is BatchRowErrorPayload {
  return (
    payload !== null &&
    typeof payload === 'object' &&
    Array.isArray((payload as BatchRowErrorPayload).rows)
  )
}

export function BatchCreateStudentsPage(): JSX.Element {
  // Success navigation goes through `leave` so the unsaved-changes guard is
  // provably stood down first — see `useLeaveAfterSave`.
  const { leaving, leave } = useLeaveAfterSave()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [fileReading, setFileReading] = useState(false)
  const [onlyProblems, setOnlyProblems] = useState(false)
  const [failure, setFailure] = useState<ResolvedFailure<BatchField> | null>(
    null,
  )
  const failureRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (formMessage(failure) !== null) failureRef.current?.focus()
  }, [failure])
  const [preflight, setPreflight] =
    useState<EnrollmentBatchPreflightResponse | null>(null)
  const [rowsModified, setRowsModified] = useState(false)
  // A response to a superseded cohort/email list must never restore stale
  // classifications after the operator has already edited the wave.
  const reviewGeneration = useRef(0)

  const form = useForm<
    CreateStudentBatchFormInput,
    unknown,
    CreateStudentBatchForm
  >({
    resolver: zodResolver(createStudentBatchSchema),
    mode: 'onSubmit',
    // `?clientId=` carries the context in from a client page or a
    // filtered roster; absent that, the picker starts UNCHOSEN (see the
    // schema comment — deliberately no home-client fallback). A cohort
    // page pre-answers BOTH steps with `?cohortId=` on top.
    defaultValues: {
      clientId: searchParams.get('clientId') ?? '',
      cohortId: searchParams.get('cohortId') ?? '',
      rows: [],
    },
  })
  const {
    control,
    handleSubmit,
    formState,
    setError,
    setValue,
    getValues,
    getFieldState,
    trigger,
    watch,
  } = form
  const rowsArray = useFieldArray({ control, name: 'rows' })
  const rowErrors = formState.errors.rows

  const clientId = watch('clientId')

  // A cohort id cannot outlive a change of client — it would 404, and the
  // composite FK makes it unwritable regardless. Same rule as the single
  // form and the roster filter, and now the same implementation: the helper
  // is what keeps the mount run from wiping the `?cohortId=` a cohort page
  // just handed us.
  useResetOnChange(clientId, () => {
    setValue('cohortId', '')
  })

  // The cohort is REQUIRED here, so unlike the single-create form a failed
  // cohort fetch does block this page — but it degrades to a message and a
  // working Back link, never a blank screen.
  const cohortId = form.watch('cohortId')
  // Keep every cohort visible. Unusable rows are disabled with their repair
  // reason, so operators can tell "this client has cohorts, but none are
  // ready" from "this client has no cohorts" without leaving the form.
  const loadedCohort = useCohort(clientId, cohortId).data ?? undefined
  const selectedCohort = loadedCohort === undefined
    ? undefined
    : { ...loadedCohort, ...preflight?.cohort }
  // These counts cover the whole client, not the picker's bounded search page.
  const clientQuery = useClient(clientId)
  const loadedClient = clientQuery.data ?? undefined
  const clientCounts = loadedClient === undefined ? undefined : {
    ...loadedClient,
    ...(preflight === null ? {} : {
      clientEmployeeIdEnabled: preflight.clientEmployeeIdEnabled,
      clientDepartmentEnabled: preflight.clientDepartmentEnabled,
    }),
  }
  const clientSettingsReady = clientQuery.isSuccess && clientCounts !== undefined
  const disabledCompanyFields = clientCounts === undefined ? [] : [
    ...(!clientCounts.clientEmployeeIdEnabled ? ['Employee ID'] : []),
    ...(!clientCounts.clientDepartmentEnabled ? ['Department'] : []),
  ]
  const businessToday = todayJstIso()
  const selectedCohortProblems =
    selectedCohort === undefined
      ? []
      : cohortEnrollmentReadinessProblems(selectedCohort, businessToday)
  const selectedCohortReady =
    selectedCohort !== undefined && selectedCohortProblems.length === 0
  const noEnrollableCohort =
    cohortId === '' &&
    clientCounts !== undefined &&
    clientCounts.cohortCount > 0 &&
    clientCounts.enrollableCohortCount === 0
  const cohortReadinessMessage =
    selectedCohortProblems.length > 0
      ? selectedCohortProblems.join(' ')
      : noEnrollableCohort
        ? 'This client does not currently have an enrollable cohort. Update an existing cohort or create a new one before enrolling students.'
        : null

  const preflightCheck = useMutation({
    mutationFn: async ({ values, generation }: { values: CreateStudentBatchForm; generation: number }) => {
      let rows = values.rows
      // Default placement choices are derived from the first classification.
      // Request a new token if those choices change the submitted input.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const review = await adminStudentClient.preflightEnrollmentBatch(values.clientId, {
          cohortId: values.cohortId, rows,
        })
        if (generation !== reviewGeneration.current) return { review, generation, rows }
        const resolved = rows.map((row, index) => {
          const outcome = review.rows[index]
          const reuseAvailable = outcome?.classification === 'returning_student'
            && outcome.reusablePlacementLevel !== null
          const operatorChose = getFieldState(`rows.${index}.placementRequirement`).isDirty
          const placementRequirement = operatorChose
            && (row.placementRequirement !== 'reuse_latest_result' || reuseAvailable)
            ? row.placementRequirement
            : reuseAvailable ? 'reuse_latest_result' as const : 'require_new_diagnostic' as const
          return { ...row, placementRequirement }
        })
        if (resolved.every((row, index) => row.placementRequirement === rows[index]?.placementRequirement)) {
          return { review, generation, rows }
        }
        rows = resolved
      }
      throw new Error('The student results changed while checking. Check rows again.')
    },
    onSuccess: ({ review, generation, rows }) => {
      if (generation !== reviewGeneration.current) return
      setFailure(null)
      setPreflight(review)
      setRowsModified(false)
      rows.forEach((row, index) => {
        const path = `rows.${index}.placementRequirement` as const
        setValue(path, row.placementRequirement, { shouldDirty: getFieldState(path).isDirty })
      })
    },
    onError: (err: unknown, { generation }) => {
      if (generation !== reviewGeneration.current) return
      setPreflight(null)
      setFailure(resolveFailure(err, BATCH_ERROR_FIELD, messageForStudentError))
    },
  })

  function invalidateReview(rowsChanged = true): void {
    reviewGeneration.current += 1
    setPreflight(null)
    if (!rowsChanged) setRowsModified(false)
    else if (preflight !== null || preflightCheck.isPending) setRowsModified(true)
  }

  // An import belongs to its selected client/cohort. Reset the form baseline
  // as well as the review so corrections and placement choices cannot carry
  // into another cohort, and late responses cannot repopulate the old wave.
  useResetOnChange(`${clientId}:${cohortId}`, () => {
    invalidateReview(false)
    preflightCheck.reset()
    form.reset({ clientId: getValues('clientId'), cohortId: getValues('cohortId'), rows: [] })
    setFailure(null)
    setOnlyProblems(false)
    setFileReading(false)
  })

  function validateCompanyAvailability(values: CreateStudentBatchForm): boolean {
    if (!clientSettingsReady) return false
    let valid = true
    values.rows.forEach((row, index) => {
      for (const key of ['clientEmployeeId', 'clientDepartment'] as const) {
        if (row[key]?.trim() && !clientCounts?.[`${key}Enabled`]) {
          valid = false
          const label = key === 'clientEmployeeId' ? 'Employee ID' : 'Department'
          setError(`rows.${index}.${key}`, {
            type: 'availability',
            message: `${label} must be empty because it is disabled for this client.`,
          })
        }
      }
    })
    return valid
  }

  function reviewRows(values: CreateStudentBatchForm): void {
    const generation = reviewGeneration.current + 1
    reviewGeneration.current = generation
    setFailure(null)
    setPreflight(null)
    if (validateCompanyAvailability(values)) preflightCheck.mutate({ values, generation })
  }

  async function reviewCurrentRows(): Promise<void> {
    const valid = await trigger()
    if (!valid) return
    const parsed = createStudentBatchSchema.safeParse(getValues())
    if (!parsed.success) return
    // Put Zod's trim/lowercase/default output back into the editable table so
    // what the operator reviews is exactly what the API receives.
    parsed.data.rows.forEach((row, index) => {
      setValue(`rows.${index}.clientEmployeeId`, row.clientEmployeeId)
      setValue(`rows.${index}.clientDepartment`, row.clientDepartment)
      setValue(`rows.${index}.email`, row.email)
      setValue(`rows.${index}.firstName`, row.firstName)
      setValue(`rows.${index}.lastName`, row.lastName)
      setValue(`rows.${index}.username`, row.username)
      setValue(`rows.${index}.addressSuffix`, row.addressSuffix)
    })
    reviewRows(parsed.data)
  }

  const create = useMutation({
    mutationFn: (values: CreateStudentBatchForm) =>
      adminStudentClient.createEnrollmentBatch(values.clientId, {
        cohortId: values.cohortId,
        rows: values.rows,
        previewToken: preflight!.previewToken,
      }),
    onSuccess: (created) => {
      for (const key of keysStaleAfterStudentChange(created.clientId)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      // The confirmation OUTLIVES this page (we navigate to the roster),
      // so it goes to a toast — the one thing the toast is for. Counts
      // and the send day, not "success": numbers are what an operator
      // checks against the list they uploaded.
      // The resolved row, not a lookup in a result page the chosen cohort
      // need not be on.
      const cohortName = selectedCohort?.name
      const newStudents = created.rows.filter(
        (row) => row.identityCreated,
      ).length
      const returning = created.created - newStudents
      const keptInvitations = created.rows.filter(row => row.notificationState === 'existing_activation').length
      toast({
        variant: 'success',
        description:
        `${created.created} ${created.created === 1 ? 'enrollment' : 'enrollments'} created` +
          `${cohortName !== undefined ? ` in ${cohortName}` : ''}: ${newStudents} new ${newStudents === 1 ? 'student' : 'students'}, ${returning} returning ${returning === 1 ? 'student' : 'students'}.` +
          (keptInvitations > 0 ? ` ${keptInvitations} ${keptInvitations === 1 ? 'student keeps an existing activation invitation' : 'students keep existing activation invitations'} with the original send schedule. See the roster for invitation dates.` : ''),
      })
      // Land on the wave, not the client's entire population: the operator
      // can immediately reconcile the uploaded list and invitation states.
      const rosterFilters = new URLSearchParams({
        clientId: created.clientId,
        cohortId: created.cohortId,
      })
      leave(`/admin/students?${rosterFilters.toString()}`)
    },
    onError: (err: unknown) => {
      if (err instanceof AdminApiError && err.detail === 'batch_preview_changed') {
        invalidateReview(false)
        void clientQuery.refetch()
        void queryClient.invalidateQueries({ queryKey: adminKeys.cohort(clientId, cohortId) })
      }
      // Row-scoped refusals anchor to their table rows — the payload
      // carries 0-based indexes into the list we submitted. The table's
      // own problem count reports them, so no duplicate summary on top.
      if (err instanceof AdminApiError && isRowPayload(err.payload)) {
        const rowMessage =
          (err.detail !== undefined
            ? ROW_CODE_MESSAGE[err.detail]
            : undefined) ?? err.message
        const field = err.detail === 'invalid_name' ? 'lastName' : 'email'
        for (const index of (err.payload as BatchRowErrorPayload).rows ?? []) {
          setError(`rows.${index}.${field}`, {
            type: 'server',
            message: rowMessage,
          })
        }
        return
      }
      setFailure(resolveFailure(err, BATCH_ERROR_FIELD, messageForStudentError))
    },
  })

  function importRows(rows: EnrollmentFileRow[]): void {
    invalidateReview(false)
    setFailure(null)
    setOnlyProblems(false)
    const parsed = createStudentBatchSchema.safeParse({
      clientId: getValues('clientId'), cohortId: getValues('cohortId'), rows,
    })
    rowsArray.replace(parsed.success ? parsed.data.rows.map((row, index) => ({ ...row, sourceRow: rows[index]?.sourceRow })) : rows)
    if (parsed.success) reviewRows(parsed.data)
    else void trigger('rows')
  }

  const rowCount = rowsArray.fields.length
  const problemCount = Array.isArray(rowErrors)
    ? rowErrors.filter((entry) => entry !== undefined).length
    : 0
  const preflightConflictCount = preflight?.conflicts ?? 0
  const attentionCount = problemCount + preflightConflictCount
  const submitting = create.isPending

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_WORKFLOW_PAGE}>
        <PageHeader title="Enroll several students" />

        <fieldset disabled={submitting} className="min-w-0 bg-surface-card rounded-token-card p-6">
          <form
            onSubmit={handleSubmit((values) => {
              setFailure(null)
              if (
                create.isPending || fileReading ||
                !selectedCohortReady ||
                preflight === null ||
                preflight.conflicts > 0
              )
                return
              if (validateCompanyAvailability(values)) create.mutate(values)
            })}
            className="space-y-4"
            aria-label="Enroll several students"
            noValidate
          >
            {formMessage(failure) !== null && (
              <div ref={failureRef} tabIndex={-1}>
                <FormError message={formMessage(failure)} />
              </div>
            )}
            {clientId !== '' && !clientSettingsReady && (
              <Notice variant={clientQuery.isError ? 'danger' : 'neutral'} action={clientQuery.isError ? <NoticeAction onClick={() => void clientQuery.refetch()}>Retry client settings</NoticeAction> : undefined}>
                {clientQuery.isError
                  ? 'Client settings could not be loaded.'
                  : clientQuery.isSuccess
                    ? 'Client is no longer available.'
                    : 'Loading client settings…'}
              </Notice>
            )}

            <Notice variant="neutral">
              <p className="font-medium text-text-primary">
                New and returning students
              </p>
              <p className="mt-0.5">
                Every row gets a new cohort enrollment. Returning students
                keep their sign-in and learning history; new students receive
                an activation invitation.
              </p>
            </Notice>

            <div>
              <label
                htmlFor="batch-client"
                className="block text-body font-medium mb-1"
              >
                Client
              </label>
              <Controller
                control={control}
                name="clientId"
                render={({ field }) => (
                  <ClientPicker
                    id="batch-client"
                    label="Client"
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value)
                      invalidateReview()
                    }}
                    required
                  />
                )}
              />
              <FieldError
                message={formState.errors.clientId?.message ?? null}
              />
            </div>

            <div>
              <label
                htmlFor="batch-cohort"
                className="block text-body font-medium mb-1"
              >
                Cohort
              </label>
              {clientId === '' ? (
                <p className="text-text-secondary text-body">
                  Choose a client first — its cohorts appear here.
                </p>
              ) : clientCounts?.cohortCount === 0 ? (
                <p className="text-text-secondary text-body">
                  This client has no cohorts yet.{' '}
                  <Link
                    to={`/admin/clients/${clientId}/cohorts/new`}
                    className="underline"
                  >
                    Create a cohort
                  </Link>{' '}
                  first: it carries the start date, access end, tier and
                  send schedule this wave inherits.
                </p>
              ) : (
                <Controller
                  control={control}
                  name="cohortId"
                  render={({ field }) => (
                    <CohortPicker
                      id="batch-cohort"
                      label="Cohort"
                      clientId={clientId}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)
                        invalidateReview()
                      }}
                      requireCompleteEnrollmentDefaults
                      ariaInvalid={
                        formState.errors.cohortId !== undefined ||
                        cohortReadinessMessage !== null ||
                        undefined
                      }
                      ariaDescribedby={
                        cohortReadinessMessage !== null
                          ? 'batch-cohort-readiness-error'
                          : undefined
                      }
                    />
                  )}
                />
              )}
              <FieldError
                message={
                  formState.errors.cohortId?.message ??
                  messageFor(failure, 'cohortId')
                }
              />
              <FieldError
                id="batch-cohort-readiness-error"
                message={cohortReadinessMessage}
              />
            </div>

            <AccessWindowPanel
              titleId="batch-program-access-window-title"
              windowLength={
                selectedCohort === undefined
                  ? null
                  : (formatWindowLength(
                      selectedCohort.scheduledStartOn,
                      selectedCohort.accessEndsOn,
                    ) ?? 'Not fixed')
              }
              badge={
                selectedCohort === undefined
                  ? clientId === ''
                    ? 'Choose a client'
                    : 'Choose a cohort'
                  : `Cohort: ${selectedCohort.name}`
              }
              footer={
                <InvitationScheduleSummary
                  source={
                    selectedCohort === undefined
                      ? { kind: 'unselected' }
                      : { kind: 'cohort', cohort: selectedCohort }
                  }
                />
              }
            >
              <AccessWindowCell label="Access starts">
                {selectedCohort === undefined
                  ? 'Choose a cohort'
                  : formatBusinessDate(
                      selectedCohort.scheduledStartOn,
                      'Not set',
                    )}
              </AccessWindowCell>
              <AccessWindowCell label="Access ends">
                {selectedCohort === undefined
                  ? 'Choose a cohort'
                  : formatBusinessDate(selectedCohort.accessEndsOn)}
              </AccessWindowCell>
            </AccessWindowPanel>

            {selectedCohort !== undefined && selectedCohortReady && (
              <CohortEnrollmentDefaultsPreview
                cohort={selectedCohort}
                includeAccessWindow={false}
                includeLearningContinuity={false}
              />
            )}

            <Notice variant="neutral">
              New invitation emails follow the schedule above. An existing invitation email may be
              reused if it has already been sent or is scheduled for the same date or earlier.
            </Notice>

            <EnrollmentFileInput
              key={`${clientId}:${cohortId}`}
              disabled={!selectedCohortReady || !clientSettingsReady || submitting}
              hasRows={rowCount > 0}
              onImport={importRows}
              onReadingChange={setFileReading}
            />
            {disabledCompanyFields.length > 0 && (
              <p className="text-body text-text-secondary">{disabledCompanyFields.join(' and ')} must be empty for this client.</p>
            )}
            {selectedCohortReady && rowCount > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-body" role="status">
                    {rowCount} {rowCount === 1 ? 'student' : 'students'}
                    {preflightCheck.isPending
                      ? ' — checking identities and enrollments…'
                      : attentionCount > 0
                        ? ` — ${attentionCount} ${attentionCount === 1 ? 'row needs' : 'rows need'} attention`
                        : preflight !== null ? ' — ready to create' : ''}
                  </p>
                </div>
                <FieldError
                  message={
                    Array.isArray(rowErrors)
                      ? null
                      : (rowErrors?.message ?? rowErrors?.root?.message ?? null)
                  }
                />
                {preflight !== null && (
                  <div className="mb-3 rounded-token-card bg-surface-app p-3">
                    <div className="flex flex-wrap items-center gap-2 text-body">
                      <span className="font-medium">Wave review</span>
                      <Badge variant="neutral">
                        {preflight.newStudents} new
                      </Badge>
                      <Badge variant="muted">
                        {preflight.returningStudents} returning
                      </Badge>
                      {preflight.conflicts > 0 && (
                        <Badge variant="danger">
                          {preflight.conflicts}{' '}
                          {preflight.conflicts === 1 ? 'conflict' : 'conflicts'}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-caption text-text-secondary">
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="placement" size={14} />
                        Placement is reviewed per returning student
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="program" size={14} />
                        New curriculum attempt
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="trophy" size={14} />
                        Trophy progress starts at 0
                      </span>
                    </div>
                  </div>
                )}
                <label className="mb-2 flex items-center gap-2 text-body">
                  <Switch checked={onlyProblems} onCheckedChange={setOnlyProblems} />
                  Only show problems
                </label>
                <div className={TABLE_WRAP}>
                  <table className={TABLE}>
                    <thead>
                      <tr className={TABLE_HEAD_ROW}>
                        <th className="w-8 text-left font-medium p-2">File row</th>
                        <th className="text-left font-medium p-2">Student</th>
                        <th className="text-left font-medium p-2">Email</th>
                        <th className="text-left font-medium p-2">
                          Family name
                        </th>
                        <th className="text-left font-medium p-2">Given name</th>
                        <th className="text-left font-medium p-2">
                          Preferred name (optional)
                        </th>
                        <th className="text-left font-medium p-2">Suffix</th>
                        {(['clientEmployeeId', 'clientDepartment'] as const).map((key) => (
                          <th key={key} className="text-left font-medium p-2">
                            <div className="flex items-center justify-between gap-2">
                              {key === 'clientEmployeeId' ? 'Employee ID' : 'Department'}
                              <Badge variant={clientCounts?.[`${key}Enabled`] ? 'neutral' : 'muted'}>
                                {clientCounts?.[`${key}Enabled`] ? 'Enabled' : 'Disabled'}
                              </Badge>
                            </div>
                          </th>
                        ))}
                        <th className="min-w-56 text-left font-medium p-2">
                          Placement
                        </th>
                        <th className="w-20 p-2">
                          <span className="sr-only">Row actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsArray.fields.map((row, index) => {
                        if (onlyProblems && !(Array.isArray(rowErrors) && rowErrors[index]) && preflight?.rows[index]?.classification !== 'conflict') return null
                        const reviewed = preflight?.rows[index]
                        return (
                          <tr key={row.id} className={TABLE_ROW_NESTED}>
                            <td className="p-2 text-text-secondary text-table align-top pt-4">
                              {row.sourceRow ?? index + 1}
                            </td>
                            <td className="min-w-48 p-2 align-top pt-3">
                              {reviewed === undefined ? (
                                <span className="text-table-secondary text-text-secondary">
                                  Awaiting check
                                </span>
                              ) : reviewed.classification === 'new_student' ? (
                                <Badge variant="neutral">New student</Badge>
                              ) : reviewed.classification ===
                                'returning_student' ? (
                                <>
                                  <Badge variant="muted">Returning</Badge>
                                  <p className="mt-1 text-table-secondary text-text-secondary">
                                    Existing profile: {reviewed.studentName}. The
                                    imported identity does not rename it.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <Badge variant="danger">Conflict</Badge>
                                  {reviewed.conflictCode === 'client_company_information_conflict' ? (<p className="mt-1 text-table-secondary text-danger-text">Company information differs from the saved record. Correct the imported values or <Link className="underline" to={`/admin/students/${reviewed.userId}/edit`}>edit the student</Link> before retrying.</p>) : reviewed.conflictCode === 'period_overlap' ? (
                                    <p className="mt-1 text-table-secondary text-danger-text">
                                      Overlaps{' '}
                                      {formatBusinessDate(
                                        reviewed.conflictingStartsOn,
                                      )}{' '}
                                      →{' '}
                                      {formatBusinessDate(
                                        reviewed.conflictingEndsOn,
                                      )}
                                      .{' '}
                                      {reviewed.userId !== null && (
                                        <Link
                                          to={`/admin/students/${reviewed.userId}/edit`}
                                          className="font-medium underline"
                                        >
                                          Review student
                                        </Link>
                                      )}{' '}
                                      to end and replace it, or remove this row.
                                    </p>
                                  ) : (
                                    <p className="mt-1 text-table-secondary text-danger-text">
                                      This student cannot be enrolled in this
                                      client.
                                    </p>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="min-w-64 p-2 align-top">
                              <label
                                htmlFor={`batch-row-${index}-email`}
                                className="sr-only"
                              >
                                Row {index + 1} email
                              </label>
                              <Controller
                                control={control}
                                name={`rows.${index}.email`}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id={`batch-row-${index}-email`}
                                    type="email"
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      invalidateReview()
                                      // A FLAGGED field re-validates as it is
                                      // edited; pristine fields stay quiet.
                                      if (fieldState.error !== undefined)
                                        void trigger(`rows.${index}.email`)
                                    }}
                                    required
                                  />
                                )}
                              />
                              <FieldError
                                message={
                                  Array.isArray(rowErrors)
                                    ? (rowErrors[index]?.email?.message ?? null)
                                    : null
                                }
                              />
                            </td>
                            <td className="min-w-48 p-2 align-top">
                              <label
                                htmlFor={`batch-row-${index}-lastName`}
                                className="sr-only"
                              >
                                Row {index + 1} family name
                              </label>
                              <Controller
                                control={control}
                                name={`rows.${index}.lastName`}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id={`batch-row-${index}-lastName`}
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      invalidateReview()
                                      if (fieldState.error !== undefined)
                                        void trigger(`rows.${index}.lastName`)
                                    }}
                                    required
                                  />
                                )}
                              />
                              <FieldError
                                message={
                                  Array.isArray(rowErrors)
                                    ? (rowErrors[index]?.lastName?.message ??
                                      null)
                                    : null
                                }
                              />
                            </td>
                            <td className="min-w-48 p-2 align-top">
                              <label
                                htmlFor={`batch-row-${index}-firstName`}
                                className="sr-only"
                              >
                                Row {index + 1} given name
                              </label>
                              <Controller
                                control={control}
                                name={`rows.${index}.firstName`}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id={`batch-row-${index}-firstName`}
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      invalidateReview()
                                      if (fieldState.error !== undefined)
                                        void trigger(`rows.${index}.firstName`)
                                    }}
                                    required
                                  />
                                )}
                              />
                              <FieldError
                                message={
                                  Array.isArray(rowErrors)
                                    ? (rowErrors[index]?.firstName?.message ??
                                      null)
                                    : null
                                }
                              />
                            </td>
                            <td className="min-w-48 p-2 align-top">
                              <label
                                htmlFor={`batch-row-${index}-username`}
                                className="sr-only"
                              >
                                Row {index + 1} preferred name (optional)
                              </label>
                              <Controller
                                control={control}
                                name={`rows.${index}.username`}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id={`batch-row-${index}-username`}
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e)
                                      invalidateReview()
                                      if (fieldState.error !== undefined)
                                        void trigger(`rows.${index}.username`)
                                    }}
                                  />
                                )}
                              />
                              <FieldError
                                message={
                                  Array.isArray(rowErrors)
                                    ? (rowErrors[index]?.username?.message ??
                                      null)
                                    : null
                                }
                              />
                            </td>
                            <td className="min-w-32 p-2 align-top">
                              <label
                                htmlFor={`batch-row-${index}-suffix`}
                                className="sr-only"
                              >
                                Row {index + 1} suffix
                              </label>
                              <Controller
                                control={control}
                                name={`rows.${index}.addressSuffix`}
                                render={({ field }) => (
                                  <Select
                                    value={getValues(`rows.${index}.parseErrorField`) === 'addressSuffix' && getValues(`rows.${index}.parseError`) ? '' : field.value}
                                    onValueChange={value => {
                                      field.onChange(value)
                                      setValue(`rows.${index}.parseError`, undefined)
                                      invalidateReview()
                                      void trigger(`rows.${index}`)
                                    }}
                                  >
                                    <SelectTrigger
                                      id={`batch-row-${index}-suffix`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="san">san</SelectItem>
                                      <SelectItem value="none">
                                        No suffix
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                              <FieldError
                                message={
                                  Array.isArray(rowErrors)
                                    ? (rowErrors[index]?.addressSuffix
                                        ?.message ?? null)
                                    : null
                                }
                              />
                            </td>
                            {(['clientEmployeeId', 'clientDepartment'] as const).map((key) => (
                              <td key={key} className="min-w-48 p-2 align-top">
                                <Input
                                  id={`batch-${index}-${key}`}
                                  aria-label={`${key === 'clientEmployeeId' ? 'Employee ID' : 'Department'}, row ${index + 1}`}
                                  placeholder={clientCounts?.[`${key}Enabled`] ? 'Optional' : 'Must be empty'}
                                  {...form.register(`rows.${index}.${key}`, { onChange: () => {
                                    form.clearErrors(`rows.${index}.${key}`)
                                    invalidateReview()
                                  } })}
                                  maxLength={key === 'clientEmployeeId' ? 100 : 200}
                                />
                                <FieldError message={rowErrors?.[index]?.[key]?.message ?? null} />
                              </td>
                            ))}

                            <td className="min-w-56 p-2 align-top pt-3">
                              {reviewed === undefined ? (
                                <span className="text-table-secondary text-text-secondary">
                                  Awaiting check
                                </span>
                              ) : reviewed.classification === 'conflict' ? (
                                <span className="text-text-secondary">—</span>
                              ) : reviewed.classification === 'new_student' ? (
                                <>
                                  <span className="text-table font-medium">
                                    New diagnostic
                                  </span>
                                  <p className="mt-0.5 text-table-secondary text-text-secondary">
                                    Required for a first enrollment.
                                  </p>
                                </>
                              ) : reviewed.reusablePlacementLevel === null ? (
                                <>
                                  <span className="text-table font-medium">
                                    New diagnostic
                                  </span>
                                  <p className="mt-0.5 text-table-secondary text-text-secondary">
                                    No compatible result is available.
                                  </p>
                                </>
                              ) : (
                                <Controller
                                  control={control}
                                  name={`rows.${index}.placementRequirement`}
                                  render={({ field }) => (
                                    <>
                                      <label
                                        htmlFor={`batch-row-${index}-placement`}
                                        className="sr-only"
                                      >
                                        Row {index + 1} placement
                                      </label>
                                      <Select
                                        value={
                                          field.value ?? 'require_new_diagnostic'
                                        }
                                        onValueChange={(value) => {
                                          field.onChange(value)
                                          invalidateReview()
                                          void reviewCurrentRows()
                                        }}
                                      >
                                        <SelectTrigger
                                          id={`batch-row-${index}-placement`}
                                          size="sm"
                                          surface="raised"
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="reuse_latest_result">
                                            <span>
                                              Reuse latest result:{' '}
                                              <strong>
                                                {reviewed.reusablePlacementLevel}
                                              </strong>
                                            </span>
                                          </SelectItem>
                                          <SelectItem value="require_new_diagnostic">
                                            Require new diagnostic
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <p className="mt-1 text-table-secondary text-text-secondary">
                                        Reuse affects placement only; curriculum
                                        and trophies still start fresh.
                                      </p>
                                    </>
                                  )}
                                />
                              )}
                            </td>
                            <td className="p-2 text-right align-top pt-3">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Remove row ${index + 1}`}
                                title="Remove row"
                                onClick={() => {
                                  rowsArray.remove(index)
                                  invalidateReview()
                                }}
                              >
                                <Icon name="delete" />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      rowsArray.append({
                        sourceRow: Math.max(1, ...rowsArray.fields.map(row => row.sourceRow ?? 1)) + 1,
                        email: '',
                        firstName: '',
                        lastName: '',
                        username: '',
                        addressSuffix: 'san',
                        placementRequirement: 'require_new_diagnostic',
                      })
                      invalidateReview()
                    }}
                  >
                    <Icon name="add" />
                    Add a row
                  </Button>
                  {preflight === null &&
                    !preflightCheck.isPending &&
                    problemCount === 0 &&
                    rowCount > 0 && (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!clientSettingsReady}
                          onClick={() => void reviewCurrentRows()}
                        >
                          Check rows
                        </Button>
                        {rowsModified && (
                          <span role="status" className="text-body text-text-secondary">
                            Some rows were modified. Check rows again.
                          </span>
                        )}
                      </>
                    )}
                </div>
              </div>
            )}

            {selectedCohortReady && rowCount > 0 && (
              <ProvisioningFormActions
                cancelTo="/admin/students"
                help={
                  <p>
                    {problemCount > 0
                      ? `Fix the ${
                          problemCount === 1
                            ? 'flagged row'
                            : `${problemCount} flagged rows`
                        } first — it enrolls every row or none. Notifications follow the cohort’s send schedule.`
                      : preflightCheck.isPending
                        ? 'Checking students, reusable placement, and date conflicts…'
                        : preflight === null
                          ? 'Check the rows again before creating the wave.'
                          : preflight.conflicts > 0
                            ? `Resolve or remove the ${preflight.conflicts === 1 ? 'conflicting row' : `${preflight.conflicts} conflicting rows`} first — it enrolls every row or none.`
                            : 'Creates every enrollment or none. Returning students keep their sign-in and learning history; notifications follow the cohort’s send schedule.'}
                  </p>
                }
              >
                {/* Known-refused → disabled with the reason: the rows were
                    validated at parse time, so a submit that will fail is a
                    click and an error the operator does not need. */}
                <SubmitButton
                  pending={submitting}
                  pendingLabel="Creating…"
                  disabled={
                    !clientSettingsReady ||
                    fileReading || rowCount === 0 ||
                    problemCount > 0 ||
                    preflightCheck.isPending ||
                    preflight === null ||
                    preflight.conflicts > 0
                  }
                >
                  {`Create ${rowCount} ${rowCount === 1 ? 'enrollment' : 'enrollments'}`}
                </SubmitButton>
              </ProvisioningFormActions>
            )}
          </form>
        </fieldset>

        {/* Uploaded rows count as unsaved work until enrollment succeeds. */}
        <UnsavedChangesGuard
          when={(formState.isDirty || rowCount > 0 || fileReading) && !leaving}
          name="the cohort enrollments"
        />
      </div>
    </main>
  )
}
